import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { App } from './App.js';
import './styles/theme.css';
import './styles/layout.css';

// ────────────────────────────────────────────────────────────────────────────
// Sentry — error monitoring + performance tracing (opsional)
//
// Setup:
//   1. Buat project di https://sentry.io
//   2. Salin DSN dari Settings → Projects → [project] → Client Keys (DSN)
//   3. Set di GitHub Variables (bukan Secrets — DSN aman publik):
//      VITE_SENTRY_DSN = "https://xxx@o123456.ingest.sentry.io/789"
//   4. Re-deploy (GitHub Actions akan inject VITE_SENTRY_DSN saat build)
//
// Development lokal: buat file apps/web/.env.local:
//   VITE_SENTRY_DSN=https://xxx@o123456.ingest.sentry.io/789
//
// Jika VITE_SENTRY_DSN tidak diset, Sentry tidak aktif — tidak ada error yang
// dikirim ke sentry.io dan tidak ada overhead performance.
// ────────────────────────────────────────────────────────────────────────────

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

Sentry.init({
  dsn: SENTRY_DSN ?? '',

  // Nonaktifkan kalau DSN tidak diset (dev lokal tanpa Sentry)
  enabled: Boolean(SENTRY_DSN),

  // Environment tag — tampil di Sentry UI untuk filter
  environment: import.meta.env.MODE,

  integrations: [
    // React Router v6 tracing — track navigasi antar halaman sebagai transactions.
    // HashRouter (/#/path) didukung via useLocation + useNavigationType hook.
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),

    // Capture console.error() sebagai Sentry breadcrumbs (bukan events)
    Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }),
  ],

  // Capture 100% traces di production — turunkan ke 0.1 kalau volume tinggi
  tracesSampleRate: 1.0,

  // Tidak report error dari browser extension atau localhost
  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /^moz-extension:\/\//i,
  ],

  // Before sending event — bisa tambahkan custom data atau filter di sini
  beforeSend(event) {
    // Filter out auth errors (401) — bukan bug, behavior yang disengaja
    if (event.request?.url?.includes('api/bff') && event.message?.includes('401')) {
      return null;
    }
    return event;
  },
});

// ────────────────────────────────────────────────────────────────────────────
// Render app
// ────────────────────────────────────────────────────────────────────────────

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Elemen #root tidak ditemukan di index.html');

// Wrap App dengan Sentry.ErrorBoundary supaya crash React terlapor ke Sentry
// dan user mendapat fallback UI alih-alih white screen.
createRoot(rootEl).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div
          style={{
            padding: '24px',
            maxWidth: '480px',
            margin: '40px auto',
            fontFamily: 'system-ui, sans-serif',
            textAlign: 'center',
          }}
        >
          <h2 style={{ fontSize: '18px', marginBottom: '8px' }}>
            Terjadi kesalahan yang tidak terduga
          </h2>
          <p style={{ fontSize: '14px', color: '#888', marginBottom: '20px' }}>
            Error telah dilaporkan secara otomatis. Silakan coba muat ulang halaman.
          </p>
          {import.meta.env.DEV && (
            <pre
              style={{
                fontSize: '11px',
                background: '#f5f5f5',
                padding: '12px',
                borderRadius: '6px',
                textAlign: 'left',
                overflow: 'auto',
                maxHeight: '200px',
                marginBottom: '16px',
              }}
            >
              {error instanceof Error ? error.message : String(error)}
            </pre>
          )}
          <button
            onClick={resetError}
            style={{
              padding: '8px 20px',
              background: '#1a1a2e',
              color: '#d4af37',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Coba Lagi
          </button>
        </div>
      )}
      onError={(error, componentStack) => {
        console.error('[Sentry ErrorBoundary]', error, componentStack);
      }}
    >
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
